import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Plus, Trash2, Send, Users, Pencil, FileText, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const emptyContact = { email: "", contact_person: "", company_name: "", phone: "", notes: "" };

export default function BulkMail() {
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [openContact, setOpenContact] = useState(false);
  const [form, setForm] = useState(emptyContact);
  const [editId, setEditId] = useState(null);

  async function load() {
    const [c, t] = await Promise.all([
      api.get("/mail-contacts"),
      api.get("/mail-templates"),
    ]);
    setContacts(c.data);
    setTemplates(t.data);
  }
  useEffect(() => { load(); }, []);

  function applyTemplate(id) {
    setSelectedTemplate(id);
    const t = templates.find((x) => x.id === id);
    if (t) {
      setSubject(t.subject);
      setMessage(t.message);
    }
  }

  function toggle(id) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function selectAll() {
    if (selected.size === contacts.length) setSelected(new Set());
    else setSelected(new Set(contacts.map((c) => c.id)));
  }

  function openNew() { setForm(emptyContact); setEditId(null); setOpenContact(true); }
  function openEdit(c) { setForm({ ...emptyContact, ...c }); setEditId(c.id); setOpenContact(true); }

  async function saveContact() {
    try {
      if (!form.email || !form.contact_person) return toast.error("E-posta ve ilgili kişi zorunlu");
      if (editId) await api.put(`/mail-contacts/${editId}`, form);
      else await api.post("/mail-contacts", form);
      toast.success("Kaydedildi");
      setOpenContact(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function removeContact(id) {
    if (!window.confirm("Kişi silinsin mi?")) return;
    await api.delete(`/mail-contacts/${id}`);
    toast.success("Silindi");
    setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
    await load();
  }

  async function sendMail() {
    if (selected.size === 0) return toast.error("En az bir alıcı seçin");
    if (!subject.trim() || !message.trim()) return toast.error("Konu ve mesaj zorunlu");
    setSending(true);
    try {
      const { data } = await api.post("/mail-contacts/send", {
        contact_ids: [...selected],
        subject,
        message,
      });
      toast.success(`${data.sent}/${data.total} e-posta gönderildi`);
      if (data.sent < data.total) {
        const failed = data.results.filter((r) => r.status === "failed");
        toast.error(`Başarısız: ${failed.map((f) => f.email).join(", ")}`);
      }
      setSelected(new Set());
      setSubject("");
      setMessage("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="bulk-mail-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400">İletişim Merkezi</div>
          <h1 className="text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <Mail className="w-8 h-8 text-amber-400" /> Toplu Mail
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Müşteri kişilerinizi yönetin, seçtiğiniz alıcılara özel mesaj gönderin.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="add-contact-btn">
            <Plus className="w-4 h-4 mr-1" /> Yeni Kişi Ekle
          </Button>
          <Link to="/settings/mail">
            <Button variant="outline" className="border-white/10" data-testid="mail-settings-link">
              <Settings className="w-4 h-4 mr-1" /> Mail Ayarları
            </Button>
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
        {/* Contacts list */}
        <div className="xl:col-span-3 card-elevated rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-400" />
              <span className="font-semibold">Kişi Listesi</span>
              <span className="text-xs text-slate-500">({contacts.length})</span>
            </div>
            {contacts.length > 0 && (
              <Button size="sm" variant="ghost" onClick={selectAll} className="text-xs" data-testid="select-all-btn">
                {selected.size === contacts.length ? "Seçimi Kaldır" : "Tümünü Seç"}
              </Button>
            )}
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            {contacts.length === 0 && (
              <div className="p-10 text-center text-slate-500">
                Henüz kişi eklenmedi.<br />
                <span className="text-xs">"Yeni Kişi Ekle" ile başlayın.</span>
              </div>
            )}
            <ul className="divide-y divide-white/5">
              {contacts.map((c) => (
                <li key={c.id} className={`px-5 py-3 flex items-start gap-3 hover:bg-white/[0.02] transition ${selected.has(c.id) ? "bg-amber-500/5" : ""}`} data-testid={`contact-row-${c.id}`}>
                  <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} className="mt-1" data-testid={`contact-check-${c.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{c.contact_person}</span>
                      {c.company_name && <span className="text-xs text-slate-500">· {c.company_name}</span>}
                    </div>
                    <div className="text-sm text-slate-400 mono truncate">{c.email}</div>
                    {c.phone && <div className="text-xs text-slate-500 mt-0.5">{c.phone}</div>}
                  </div>
                  <div className="flex gap-1 opacity-60 hover:opacity-100">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => removeContact(c.id)} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Compose */}
        <div className="xl:col-span-2 card-elevated rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-4 h-4 text-amber-400" />
            <h3 className="font-semibold">Mesaj Yaz</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Hazır Şablon</Label>
              <Select value={selectedTemplate} onValueChange={applyTemplate}>
                <SelectTrigger className="bg-[#111723] border-white/10 mt-1" data-testid="template-select">
                  <SelectValue placeholder="Şablon seçin (isteğe bağlı)" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B111C] border-white/10">
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`template-${t.id}`}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-slate-500 mt-1">Seçtiğinizde konu ve mesaj otomatik doldurulur, sonra düzenleyebilirsiniz.</div>
            </div>
            <div>
              <Label>Alıcılar</Label>
              <div className="rounded-lg border border-white/10 bg-[#111723] px-3 py-2 mt-1 text-sm min-h-[42px]">
                {selected.size === 0 ? (
                  <span className="text-slate-500">Sol listeden alıcı seçin</span>
                ) : (
                  <span className="text-amber-300 font-medium">{selected.size} kişi seçildi</span>
                )}
              </div>
            </div>
            <div>
              <Label>Konu</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Örn. Yeni Kampanya Duyurusu" maxLength={120} className="bg-[#111723] border-white/10 mt-1" data-testid="mail-subject-input" />
            </div>
            <div>
              <Label>Mesaj</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Mesajınızı yazın... (paragraflar için boş satır bırakın)" rows={10} maxLength={4000} className="bg-[#111723] border-white/10 mt-1 resize-none" data-testid="mail-message-input" />
              <div className="text-[10px] text-slate-500 mt-1 mono">{message.length}/4000 karakter</div>
            </div>
            <Button
              onClick={sendMail}
              disabled={sending || selected.size === 0}
              className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              data-testid="send-bulk-mail-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? "Gönderiliyor..." : `Mail Gönder (${selected.size})`}
            </Button>
            <div className="text-[11px] text-slate-500 leading-relaxed">
              Not: E-postalar Aves Elektrik Otomasyon adına, marka şablonuyla gönderilir. Yalnızca kayıtlı iş kişilerine transactional ileti gönderiniz.
            </div>
          </div>
        </div>
      </div>

      {/* Contact dialog */}
      <Dialog open={openContact} onOpenChange={setOpenContact}>
        <DialogContent className="bg-[#0B111C] border-white/10 max-w-md" data-testid="contact-dialog">
          <DialogHeader><DialogTitle>{editId ? "Kişi Düzenle" : "Yeni Kişi"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>İlgili Kişi *</Label>
              <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="bg-[#111723] border-white/10 mt-1" data-testid="contact-person-input" />
            </div>
            <div>
              <Label>E-posta *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#111723] border-white/10 mt-1" data-testid="contact-email-input" />
            </div>
            <div>
              <Label>Firma</Label>
              <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="bg-[#111723] border-white/10 mt-1" />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#111723] border-white/10 mt-1" />
            </div>
            <div>
              <Label>Notlar</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-[#111723] border-white/10 mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenContact(false)} className="border-white/10">İptal</Button>
            <Button onClick={saveContact} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="save-contact-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
